import { FileText, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { confirmModal, Modal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { fmtDate, fmtMoney } from '../../../shared/format'
import {
  STATUS_TONE,
  useCardPreview,
  useCardStatement,
  useCardStatements,
  useCardTxns,
  useVoidCardTxn,
  type CardStatement,
  type CreditCard,
  type InterestLine,
} from '../../credit'
import { toastError } from '../../ui'
import { BankStatementsPanel } from './BankStatementsPanel'

type Tab = 'statements' | 'preview' | 'txns' | 'bank'

/** Kartın ekstre geçmişi (seçilen ekstrenin dökümü) ve kart hareketleri (kesilmemiş dönemdekiler iptal edilebilir). */
export function CardStatementsModal({
  card,
  canWrite,
  onClose,
}: {
  card: CreditCard
  canWrite: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  const [tab, setTab] = useState<Tab>('statements')
  const statements = useCardStatements(card.id)
  const [selected, setSelected] = useState<string | null>(null)
  const current = selected ?? statements.data?.[0]?.id ?? null

  return (
    <Modal
      open
      size="2xl"
      onOpenChange={(v) => !v && onClose()}
      icon={<FileText />}
      title={t('finance.credit.statementsTitle', { card: card.name })}
      description={t('finance.credit.statementsHint')}
      footer={
        <Button type="button" variant="cancel" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        <TabsList size="sm" className="mb-4">
          <TabsTrigger value="statements">{t('finance.credit.statements')}</TabsTrigger>
          <TabsTrigger value="preview">{t('finance.credit.preview')}</TabsTrigger>
          <TabsTrigger value="txns">{t('finance.credit.txns')}</TabsTrigger>
          <TabsTrigger value="bank">{t('finance.bankStatements.title')}</TabsTrigger>
        </TabsList>
      </Tabs>
      {tab === 'preview' ? (
        <PreviewPanel cardId={card.id} />
      ) : tab === 'bank' ? (
        <BankStatementsPanel type="card" id={card.id} scope={card.scope} />
      ) : tab === 'txns' ? (
        <TxnList card={card} canWrite={canWrite} />
      ) : statements.isError ? (
        <ErrorState error={statements.error} onRetry={() => void statements.refetch()} />
      ) : statements.isLoading ? (
        <Skeleton className="h-48" />
      ) : !statements.data?.length ? (
        <EmptyState
          icon={FileText}
          title={t('finance.credit.noStatement')}
          description={
            card.nextStatementDate
              ? t('finance.credit.nextStatement', { date: fmtDate(card.nextStatementDate) })
              : undefined
          }
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-[14rem_1fr]">
          <ul className="max-h-[60vh] space-y-1 overflow-y-auto">
            {statements.data.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => setSelected(s.id)}
                  className={cn(
                    'w-full rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                    current === s.id ? 'border-primary bg-primary/5' : 'hover:bg-muted/50',
                  )}
                >
                  <span className="flex items-center justify-between gap-2 font-medium">
                    {fmtDate(s.statementDate)}
                    <Badge tone={STATUS_TONE[s.status]}>{t(`finance.credit.status.${s.status}`)}</Badge>
                  </span>
                  <span className="text-muted-foreground block text-xs tabular-nums">{fmtMoney(s.totalDue)}</span>
                </button>
              </li>
            ))}
          </ul>
          {current ? <StatementDetail cardId={card.id} id={current} /> : null}
        </div>
      )}
    </Modal>
  )
}

function Row({ label, value, strong, muted }: { label: string; value: string; strong?: boolean; muted?: boolean }) {
  if (muted && Number(value) === 0) return null
  return (
    <div className={cn('flex justify-between gap-2 py-0.5', strong && 'font-semibold')}>
      <span className={strong ? '' : 'text-muted-foreground'}>{label}</span>
      <span className="tabular-nums">{fmtMoney(value)}</span>
    </div>
  )
}

function StatementDetail({ cardId, id }: { cardId: string; id: string }) {
  const q = useCardStatement(cardId, id)
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />
  if (!q.data) return <Skeleton className="h-64" />
  return <StatementBody s={q.data} />
}

/** Kesilmemiş dönem: bugüne kadarki hareketler ve işlemiş faizle ön izleme (kayıt oluşturmaz). */
function PreviewPanel({ cardId }: { cardId: string }) {
  const { t } = useTranslation()
  const q = useCardPreview(cardId)
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />
  if (!q.data) return <Skeleton className="h-64" />
  return (
    <div className="space-y-3">
      <p className="bg-muted/50 rounded-lg px-3 py-2 text-xs">{t('finance.credit.previewHint')}</p>
      <StatementBody s={q.data} />
    </div>
  )
}

function InterestBreakdown({ lines }: { lines: InterestLine[] }) {
  const { t } = useTranslation()
  if (!lines.length) return null
  return (
    <div className="rounded-lg border">
      <p className="border-b px-3 py-2 text-xs font-medium">{t('finance.credit.interestLines')}</p>
      <ul className="divide-y">
        {lines.map((l, i) => (
          <li key={`${l.kind}-${i}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-xs">
            <Badge tone={l.kind === 'late' ? 'danger' : 'neutral'}>{t(`finance.credit.interestKind.${l.kind}`)}</Badge>
            <span className="text-muted-foreground min-w-0 flex-1">
              {t('finance.credit.interestFormula', {
                base: fmtMoney(l.base),
                rate: l.rate,
                days: l.days,
                from: fmtDate(l.from),
                to: fmtDate(l.to),
              })}
            </span>
            <span className="tabular-nums">{fmtMoney(l.amount)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function StatementBody({ s }: { s: CardStatement }) {
  const { t } = useTranslation()
  return (
    <div className="min-w-0 space-y-4 text-sm">
      <div className="grid grid-cols-3 gap-3 rounded-lg border p-3">
        <div>
          <p className="text-muted-foreground text-xs">{t('finance.credit.totalDue')}</p>
          <p className="text-lg font-semibold tabular-nums">{fmtMoney(s.totalDue)}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">{t('finance.credit.minDue')}</p>
          <p className="text-lg tabular-nums">{fmtMoney(s.minDue)}</p>
        </div>
        <div>
          <p className="text-muted-foreground text-xs">{t('finance.credit.dueDate')}</p>
          <p className="text-lg">{fmtDate(s.dueDate)}</p>
        </div>
      </div>
      <p className="text-muted-foreground text-xs">
        {t('finance.credit.period', { from: fmtDate(s.periodStart), to: fmtDate(s.statementDate) })} ·{' '}
        {t('finance.credit.paidSince', { amount: fmtMoney(s.paidSince) })}
      </p>
      <div className="rounded-lg border p-3">
        <Row label={t('finance.credit.f.previous')} value={s.previousBalance} />
        <Row label={t('finance.credit.f.payments')} value={`-${s.payments}`} muted />
        <Row label={t('finance.credit.f.purchases')} value={s.purchases} />
        <Row label={t('finance.credit.f.cashAdvances')} value={s.cashAdvances} muted />
        <Row label={t('finance.credit.f.fees')} value={s.fees} muted />
        <Row label={t('finance.credit.f.refunds')} value={`-${s.refunds}`} muted />
        <Row label={t('finance.credit.f.contractInterest')} value={s.contractInterest} muted />
        <Row label={t('finance.credit.f.lateInterest')} value={s.lateInterest} muted />
        <Row label={t('finance.credit.f.cashAdvanceInterest')} value={s.cashAdvanceInterest} muted />
        <Row label={t('finance.credit.f.bsmv', { rate: s.rates.bsmv ?? '' })} value={s.bsmv} muted />
        <Row label={t('finance.credit.f.kkdf', { rate: s.rates.kkdf ?? '' })} value={s.kkdf} muted />
        <div className="mt-1 border-t pt-1">
          <Row label={t('finance.credit.totalDue')} value={s.totalDue} strong />
        </div>
        <Row label={t('finance.credit.f.future')} value={s.futureInstallments} muted />
        <Row label={t('finance.credit.f.overLimit')} value={s.overLimit} muted />
      </div>
      <InterestBreakdown lines={s.interestLines} />
      {s.lines.length ? (
        <ul className="divide-y rounded-lg border">
          {s.lines.map((l, i) => (
            <li key={`${l.date}-${i}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
              <span className="text-muted-foreground w-24 shrink-0 tabular-nums">{fmtDate(l.date)}</span>
              <span className="min-w-0 flex-1 truncate">{l.description || t(`finance.credit.kind.${l.kind}`)}</span>
              {l.of > 1 ? (
                <Badge tone="info">{t('finance.credit.installmentOf', { n: l.installment, of: l.of })}</Badge>
              ) : null}
              <span className={cn('tabular-nums', Number(l.amount) < 0 && 'text-success')}>{fmtMoney(l.amount)}</span>
            </li>
          ))}
        </ul>
      ) : null}
      <p className="text-muted-foreground text-xs">
        {t('finance.credit.ratesUsed', {
          purchase: s.rates.purchase ?? '',
          late: s.rates.late ?? '',
          cash: s.rates.cash_advance ?? '',
        })}
      </p>
    </div>
  )
}

function TxnList({ card, canWrite }: { card: CreditCard; canWrite: boolean }) {
  const { t } = useTranslation()
  const txns = useCardTxns(card.id)
  const voidTxn = useVoidCardTxn(card.id)
  if (txns.isError) return <ErrorState error={txns.error} onRetry={() => void txns.refetch()} />
  if (txns.isLoading) return <Skeleton className="h-48" />
  if (!txns.data?.length)
    return (
      <EmptyState
        icon={FileText}
        title={t('finance.credit.noTxns')}
        description={card.scope === 'company' ? t('finance.credit.companyTxnsHint') : undefined}
      />
    )
  return (
    <ul className="divide-y rounded-lg border text-sm">
      {txns.data.map((x) => (
        <li key={x.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
          <span className="text-muted-foreground w-24 shrink-0 tabular-nums">{fmtDate(x.date)}</span>
          <Badge tone={x.kind === 'payment' || x.kind === 'refund' ? 'success' : 'neutral'}>
            {t(`finance.credit.kind.${x.kind}`)}
          </Badge>
          <span className="min-w-0 flex-1 truncate">{x.description}</span>
          {x.installments > 1 ? (
            <Badge tone="info">{t('finance.credit.txn.nInstallments', { count: x.installments })}</Badge>
          ) : null}
          <span className="tabular-nums">{fmtMoney(x.amount)}</span>
          {canWrite && !x.locked ? (
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              aria-label={t('common.delete')}
              disabled={voidTxn.isPending}
              onClick={() =>
                void confirmModal({
                  title: t('finance.credit.voidTitle'),
                  description: t('finance.credit.voidBody'),
                  confirmLabel: t('common.delete'),
                  destructive: true,
                }).then(
                  (ok) =>
                    ok &&
                    voidTxn.mutate(x.id, {
                      onSuccess: () => toast.success(t('finance.credit.voided')),
                      onError: toastError,
                    }),
                )
              }
            >
              <Trash2 className="size-4" />
            </Button>
          ) : null}
        </li>
      ))}
    </ul>
  )
}
